import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import CTable from '../components/shared/c-table';
import CModal from '../components/shared/c-modal';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Departments = () => {
  // The add/edit popup opens on its first field rather than the close button.
  const nameField = useRef(null);
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [modalDept, setModalDept] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDept, setDeleteDept] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/departments');
      setDepartments(res.data.data);
    } catch (err) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setModalDept(null);
    setName('');
    setDescription('');
    setShowModal(true);
  };

  const openEditModal = (dept) => {
    setModalMode('edit');
    setModalDept(dept);
    setName(dept.name);
    setDescription(dept.description || '');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modalMode === 'add') {
        await api.post('/api/departments', { name, description });
        toast.success('Department added');
      } else {
        await api.put(`/api/departments/${modalDept.id}`, { name, description });
        toast.success('Department updated');
      }
      setShowModal(false);
      fetchDepartments();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/departments/${deleteDept.id}`);
      toast.success('Department deleted');
      setDeleteDept(null);
      fetchDepartments();
    } catch (err) {
      console.error('Department delete error:', err);
      
      // Handle different error response structures
      let errorMessage = 'Failed to delete department';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      } else if (err.response?.statusText) {
        errorMessage = err.response.statusText;
      }
      
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  if (!user?.roles?.some(r => r.name === 'super_admin')) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-gray-600">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
          <p className="text-gray-600">Manage company departments</p>
        </div>
        <button
          data-testid="departments-add-btn"
          onClick={openAddModal}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Department
        </button>
      </div>

      {/* Departments Table */}
      <CTable
        columns={[
          {
            key: 'name',
            header: 'Name',
            cellClassName: 'font-medium',
            render: (dept) => <span data-testid="departments-cell-name">{dept.name}</span>,
          },
          {
            key: 'description',
            header: 'Description',
            render: (dept) => dept.description,
          },
          {
            key: 'actions',
            header: 'Actions',
            align: 'right',
            cellClassName: 'font-medium',
            render: (dept) => (
              <>
                <button
                  data-testid="departments-edit"
                  onClick={() => openEditModal(dept)}
                  className="p-1 text-warning-600 hover:text-warning-900"
                  title="Edit"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  data-testid="departments-delete"
                  onClick={() => setDeleteDept(dept)}
                  className="p-1 text-danger-600 hover:text-danger-900 ml-2"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            ),
          },
        ]}
        data={departments}
        rowKey="id"
        loading={loading}
        getRowProps={(dept) => ({ 'data-testid': `departments-row-${dept.id}` })}
        header={
          <h3 className="text-lg font-medium text-gray-900">
            {departments.length} Department{departments.length !== 1 ? 's' : ''}
          </h3>
        }
        emptyMessage={
          <>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No departments found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first department.</p>
          </>
        }
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <CModal
          onClose={() => setShowModal(false)}
          labelledBy="department-modal-title"
          initialFocus={nameField}
          dismissible={!saving}
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowModal(false)}
          >
            &times;
          </button>
          <h2 id="department-modal-title" className="text-xl font-bold mb-4">{modalMode === 'add' ? 'Add Department' : 'Edit Department'}</h2>
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="department-name" className="block text-sm font-medium text-gray-700">Name</label>
              <input
                id="department-name"
                type="text"
                ref={nameField}
                data-testid="departments-field-name"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="department-description" className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                id="department-description"
                className="mt-1 w-full border rounded-md px-3 py-2"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
            <button
              type="submit"
              data-testid="departments-submit"
              className="w-full bg-primary-600 text-white py-2 rounded-md hover:bg-primary-700 transition-colors"
              disabled={saving}
            >
              {saving ? (modalMode === 'add' ? 'Adding...' : 'Saving...') : (modalMode === 'add' ? 'Add Department' : 'Save Changes')}
            </button>
          </form>
        </CModal>
      )}

      {/* Delete Confirmation Modal */}
      {deleteDept && (
        <CModal
          onClose={() => setDeleteDept(null)}
          labelledBy="delete-department-title"
          dismissible={!deleting}
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
          data-testid="departments-confirm-modal"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setDeleteDept(null)}
          >
            &times;
          </button>
          <h2 id="delete-department-title" className="text-xl font-bold mb-4 text-danger-700">Delete Department</h2>
          <p className="mb-4 text-gray-700">
            Are you sure you want to delete the department <b>{deleteDept.name}</b>?<br />
            <span className="text-danger-600 font-semibold">
              This action cannot be undone. You cannot delete a department if employees are assigned to it.
            </span>
          </p>
          <div className="flex justify-end gap-2">
            <button
              data-testid="departments-confirm-no"
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={() => setDeleteDept(null)}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              data-testid="departments-confirm-yes"
              className="px-4 py-2 rounded bg-danger-600 text-white hover:bg-danger-700 font-semibold"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default Departments; 